import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Building2, ClipboardList, Database, ListChecks, X } from 'lucide-react';
import { Report, UserProfile } from '../../../types';
import type { GbuAnlass, SifaGbu } from '../../../services/sifa/gbuTypes';
import { legeGbuAn, erfasseGbuZeit, markiereTaetigkeitGebucht, pruefeGutschriftAufServer } from '../../../services/sifa/gbuService';
import { gbuZeitLogId, istGbuPassend, rechneGbuBuchung, waehleGbuPositionen } from '../../../services/sifa/gbuBooking';
import { formatDauer } from '../../../services/sifa/taetigkeitenLogic';
import { isHourlyUnit } from '../../../services/serviceUnits';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { JournalMirrorRequest, queueJournalMirror } from '../../../services/serviceJournal';
import { BillingSelectionModal } from '../../BillingSelectionModal';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useRechte } from '../../../hooks/useRechte';
import { heuteIso } from '../unterweisungModulLogic';
import { useGbuModul } from './useGbuModul';
import { GbuListe } from './GbuListe';
import { GbuEditor } from './GbuEditor';
import { NeueGbuDialog } from './NeueGbuDialog';
import { MassnahmenAnsicht } from './MassnahmenAnsicht';
import { BetriebAnsicht } from './BetriebAnsicht';
import { GbuZeitBuchen, ZeitEingabe } from './GbuZeitBuchen';
import type { GbuZeitZeile } from './GbuZeiten';
import type { FreigabeMeta } from './GbuFreigabeDialog';
import { GBU_JOURNAL_SOURCE, formatTag, gbuZeitStand, istGutgeschrieben, KEINE_STUNDENPOSITION_TEXT, nachholEntscheid, neuerGbuKopf, nutzerName, viertelstunden, vorabHinweisText, vorauswahlIds, vorgabeRollen } from './gbuModulLogic';

interface Props {
  project: { id: string; name?: string; data: Report };
  userProfile: UserProfile | null;
  onBack: () => void;
  /** Schreibt die verbuchte Leistung ins Projekt. Ohne ihn wird nicht gebucht. */
  onUpdate?: (report: Report) => void;
  isReadOnly?: boolean;
}

type Reiter = 'beurteilungen' | 'massnahmen' | 'betrieb';

/**
 * Der Riegel-Aufruf ging hinaus, die Antwort kam nicht an: ob er gesetzt
 * hat, weiss hier niemand. Die Liste „Erfasste Zeiten" laedt neu und zeigt
 * es — steht dort „Gutschrift fehlt", wird dort nachgeholt (gleiche Id).
 */
const AUSGANG_UNKLAR = 'Ausgang unklar — die Liste „Erfasste Zeiten" wird neu geladen; steht dort „Gutschrift fehlt", bitte dort nachholen. Nicht von Hand nachbuchen.';

/** Was abgerechnet wird: eine neu eingegebene Zeit oder eine schon erfasste (Nachbuchen). */
type ZeitQuelle =
  | { art: 'neu'; eingabe: ZeitEingabe }
  | { art: 'vorhanden'; zeit: GbuZeitZeile };

/** Datum und Dauer der Quelle — fuer Titel, Hinweis und Vorgabemenge des Abrechnungsdialogs. */
const quellenZeit = (q: ZeitQuelle): { datum: string; dauerMinuten: number } =>
  q.art === 'neu' ? { datum: q.eingabe.datum, dauerMinuten: q.eingabe.dauerMinuten } : { datum: q.zeit.datum, dauerMinuten: q.zeit.dauerMinuten };

/**
 * Gefaehrdungsbeurteilungen eines SiFa-Projekts — duenner Container
 * (docs/sifa-gbu-plan.md §14.4 F).
 *
 * Drei Ansichten: Beurteilungen (Liste, Editor), Massnahmen (Liste ueber
 * alle GBUs, Export) und Betrieb (Kataster). Er laedt Liste, Kataster und
 * Mitarbeiterstamm und haelt den einen Vorgang, der Geld bewegt: „Zeit
 * buchen". Jede pruefbare Regel steht in gbuModulLogic.ts bzw.
 * services/sifa/gbuBooking.ts (kein jsdom — Komponenten sind nicht testbar).
 *
 * ═══ DER RIEGEL GEGEN DIE DOPPELBUCHUNG ═══════════════════════════════════
 * „Zeit buchen" legt IMMER zuerst eine Zeile im Taetigkeitsnachweis an
 * (erfasseGbuZeit). Wird eine LV-Position gewaehlt, laeuft danach der Riegel
 * `markiereTaetigkeitGebucht` (RPC sifa_taetigkeit_gebucht_markieren, nur
 * `gebucht_am is null`) — und gutgeschrieben wird NUR, wenn er genau diese
 * Taetigkeit als markiert zurueckmeldet. Erst dann `onUpdate`, danach die
 * Zweitschrift ins Journal (`source 'gbu'`) ausserhalb des try. Steht der
 * Riegel, scheitert aber die Gutschrift, sagt die Meldung ausdruecklich:
 * nicht von Hand nachbuchen.
 *
 * Nachbuchen: eine schon erfasste, nicht abgerechnete Zeit („Erfasste
 * Zeiten" im Editor) laeuft ab dem Abrechnungsdialog denselben Weg — mit
 * genau IHRER Id im Riegel und ohne eine zweite Taetigkeit anzulegen.
 */
export const GbuModul: React.FC<Props> = ({ project, userProfile, onBack, onUpdate, isReadOnly = false }) => {
  const isMobile = useIsMobile();
  const meta = project.data?.metadata;
  const { gbus, kataster, mitarbeiter, laden, fehler, uebernehmeGbu, entferneGbu, aendereKataster, setzeZuordnungen } = useGbuModul(project.id);
  const rechte = useRechte(userProfile?.role);

  const [reiter, setReiter] = useState<Reiter>('beurteilungen');
  const [offenId, setOffenId] = useState<string | null>(null);
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const [anlegenLaeuft, setAnlegenLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');
  const [zeitFuer, setZeitFuer] = useState<SifaGbu | null>(null);
  const [buchung, setBuchung] = useState<{ gbu: SifaGbu; quelle: ZeitQuelle } | null>(null);
  /** Steigt nach jedem Erfassen/Abrechnen — die Liste „Erfasste Zeiten" im Editor laedt dann neu. */
  const [zeitenStand, setZeitenStand] = useState(0);
  const [buchungLaeuft, setBuchungLaeuft] = useState(false);

  /** Riegel gegen den zweiten Fingertipp — Zustand steht erst nach dem naechsten Zeichnen. */
  const anlegenRef = useRef(false);
  const buchungRef = useRef(false);

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  // Ein Kalendertag je Oeffnen genuegt: Pruefdatum und Ampeln haengen am Tag.
  const heute = useMemo(() => heuteIso(), []);
  const name = nutzerName(userProfile);
  const companyName = (userProfile?.company_name || '').trim() || null;
  const benutzer = useMemo(() => ({ id: userProfile?.id ?? null, name: nutzerName(userProfile) }), [userProfile]);
  const kundeName = (meta?.customerName || meta?.clientName || project.name || meta?.projectName || '').trim();
  const freigabeMeta: FreigabeMeta = useMemo(() => ({
    kundeName,
    kundeAnschrift: (meta?.customerAddress || meta?.clientAddress || meta?.projectAddress || '').trim() || null,
    bueroName: (userProfile?.company_name || '').trim(),
  }), [kundeName, meta?.customerAddress, meta?.clientAddress, meta?.projectAddress, userProfile?.company_name]);
  const branche = (meta?.sifa?.branche || '').trim() || null;

  /** Das ganze LV des Projekts (bei Toechtern bucht das Modul bewusst nicht auf die Mutter). */
  const alleServices = useMemo(() => meta?.globalData?.services || [], [meta]);
  const billingServices = useMemo(() => waehleGbuPositionen(alleServices), [alleServices]);
  /** Ob der Abrechnungsdialog die Stunden vorbelegt — nur bei Stundenpositionen (vorgabeMenge). */
  /** Vorauswahl im Abrechnungsdialog: die erste zur GBU passende Position; sonst nichts — bewusst waehlen. */
  const gbuVorauswahl = useMemo(() => vorauswahlIds(billingServices, istGbuPassend), [billingServices]);
  const stundenpositionen = useMemo((): 'alle' | 'einige' | 'keine' => {
    const n = billingServices.filter((s) => isHourlyUnit(s.unit)).length;
    return n === 0 ? 'keine' : n === billingServices.length ? 'alle' : 'einige';
  }, [billingServices]);
  /** Das Protokoll des Projekts — Abgleich „Gutschrift fehlt" (gbuZeitStand). */
  const serviceLog = useMemo(() => meta?.globalData?.serviceLog || [], [meta]);
  const kannAbrechnen = !isReadOnly && !!onUpdate && billingServices.length > 0;

  // ─── Anlegen ──────────────────────────────────────────────────────────────

  const neueGbu = () => {
    if (isReadOnly) { zeigeFehler('Nur-Ansicht: In diesem Projekt wird keine Gefährdungsbeurteilung angelegt.'); return; }
    setMeldung(null);
    setAnlegenOffen(true);
  };

  const anlegen = async (eingabe: { bereichId: string; titel: string; anlass: GbuAnlass }) => {
    if (isReadOnly || anlegenRef.current) return;
    anlegenRef.current = true;
    setAnlegenLaeuft(true);
    try {
      const rollen = vorgabeRollen({ nutzerName: name, ansprechpartner: (meta?.customerContactPerson || meta?.clientContactPerson || '').trim() || null });
      const g = await legeGbuAn(project.id, neuerGbuKopf({ ...eingabe, rollen }), companyName);
      uebernehmeGbu(g);
      setAnlegenOffen(false);
      setOffenId(g.id);
    } catch (e) {
      setAnlegenOffen(false);
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      anlegenRef.current = false;
      setAnlegenLaeuft(false);
    }
  };

  // ─── Zeit buchen ──────────────────────────────────────────────────────────

  const zeitBuchen = (g: SifaGbu) => {
    if (isReadOnly || g.status === 'archiviert') return;
    setMeldung(null);
    setZeitFuer(g);
  };

  const zeitEingegeben = (g: SifaGbu, e: ZeitEingabe) => {
    setZeitFuer(null);
    // Ohne passende Position (oder ohne Schreibweg ins Projekt): nur Taetigkeitsnachweis.
    if (!kannAbrechnen) { void zeitVerbuchen(g, { art: 'neu', eingabe: e }, null, 0); return; }
    setBuchung({ gbu: g, quelle: { art: 'neu', eingabe: e } });
  };

  /** Eine schon erfasste, nicht abgerechnete Zeit nachtraeglich abrechnen — ohne zweite Taetigkeit. */
  const zeitAbrechnen = (g: SifaGbu, z: GbuZeitZeile) => {
    if (!kannAbrechnen || z.gebuchtAm) return;
    setMeldung(null);
    setBuchung({ gbu: g, quelle: { art: 'vorhanden', zeit: z } });
  };

  /**
   * Taetigkeit erfassen (nur bei neuer Zeit), dann — bei gewaehlter
   * Position — Riegel und Gutschrift. Reihenfolge und Meldungen siehe
   * Kopfkommentar. Eine schon erfasste Zeit (`vorhanden`) geht mit genau
   * IHRER Id in den Riegel; es entsteht keine zweite Taetigkeit.
   */
  const zeitVerbuchen = async (g: SifaGbu, quelle: ZeitQuelle, serviceId: string | null, menge: number) => {
    if (isReadOnly) return;
    if (buchungRef.current) return;                 // zweiter Fingertipp
    buchungRef.current = true;
    setBuchungLaeuft(true);
    const neu = quelle.art === 'neu';
    const datum = neu ? quelle.eingabe.datum : quelle.zeit.datum;
    const dauerMinuten = neu ? quelle.eingabe.dauerMinuten : quelle.zeit.dauerMinuten;
    zeigeInfo(neu ? (serviceId ? 'Zeit wird erfasst und gebucht…' : 'Zeit wird erfasst…') : 'Zeit wird abgerechnet…');
    let vormerkung: JournalMirrorRequest | null = null;
    let erfasst = !neu;
    /** Der Riegel-Aufruf ist hinausgegangen — ab hier ist ein Fehler „Ausgang unklar". */
    let riegelGestartet = false;
    try {
      const basis = { performerName: name || undefined, datum, gbuNummer: g.nummer, gbuTitel: g.titel, fassung: g.fassung };
      // 0) Rechnen ist rein und schreibt nichts — eine nicht buchbare Position haelt vor jedem Schreiben an.
      //    Die neue Taetigkeit hat noch keine Id: die Probe rechnet mit einem Platzhalter, gebucht wird mit der echten.
      const probe = serviceId ? rechneGbuBuchung(alleServices, serviceId, menge, { ...basis, taetigkeitId: neu ? 'probe' : quelle.zeit.id }) : null;
      if (serviceId && !probe) {
        zeigeFehler(`Die gewählte LV-Position ist nicht buchbar (nicht gefunden, Paket-/Pauschalposition oder Menge 0). Es wurde nichts ${neu ? 'erfasst' : 'abgerechnet'}.`);
        return;
      }

      // 1) Taetigkeitsnachweis — bei neuer Zeit immer, auch „ohne Leistungsbuchung".
      const taetigkeitId = neu
        ? await erfasseGbuZeit(project.id, g, {
          datum,
          dauerMinuten,
          beschreibung: quelle.eingabe.beschreibung,
          durchgefuehrtVon: quelle.eingabe.durchgefuehrtVon || null,
          companyName,
        })
        : quelle.zeit.id;
      erfasst = true;
      if (!serviceId || !probe || !onUpdate) {
        zeigeInfo(neu
          ? `${formatDauer(dauerMinuten)} am ${formatTag(datum)} im Tätigkeitsnachweis erfasst — ohne Leistungsbuchung${billingServices.length === 0 ? `. ${KEINE_STUNDENPOSITION_TEXT}` : "."}`
          : 'Nicht abgerechnet — die Zeit bleibt offen und lässt sich später abrechnen.');
        return;
      }

      // 2) DER RIEGEL ZUERST. Nur was die Datenbank als markiert meldet, wird gutgeschrieben.
      riegelGestartet = true;
      const gebucht = await markiereTaetigkeitGebucht([taetigkeitId], serviceId, probe.menge);
      if (!gebucht.includes(taetigkeitId)) {
        zeigeInfo('Diese Zeit war bereits abgerechnet — es wurde nichts doppelt gebucht. Fehlt die Gutschrift im Projekt, steht sie unter „Erfasste Zeiten" als „Gutschrift fehlt" und lässt sich dort nachholen.');
        return;
      }

      // 3) Steht die Gutschrift schon im Projekt (zweiter Klick, zweites Fenster)? Dann nicht noch einmal.
      if (istGutgeschrieben(taetigkeitId, meta?.globalData?.serviceLog)) {
        zeigeInfo('Diese Zeit steht schon im Leistungsstand des Projekts — es wurde nichts doppelt gutgeschrieben.');
        return;
      }

      // 4) Gutschrift ins Projekt — mit der stabilen Id `gbu-zeit-<taetigkeitId>`.
      const ergebnis = rechneGbuBuchung(alleServices, serviceId, probe.menge, { ...basis, taetigkeitId });
      if (!ergebnis) {
        zeigeFehler(
          'Die Zeit ist im Tätigkeitsnachweis als abgerechnet vermerkt, die Gutschrift ins Projekt ließ sich aber nicht berechnen. ' +
          'Sie steht unter „Erfasste Zeiten" als „Gutschrift fehlt". Bitte NICHT von Hand nachbuchen.',
        );
        return;
      }
      vormerkung = schreibeGutschrift(ergebnis);
      zeigeInfo(
        `${neu ? `${formatDauer(dauerMinuten)} im Tätigkeitsnachweis erfasst und ` : `Zeit vom ${formatTag(datum)}: `}` +
        `${ergebnis.menge.toLocaleString('de-DE')} ${ergebnis.stundenposition ? 'Std.' : 'Stück'} auf ${positionText(serviceId)} gebucht.`,
      );
    } catch (err) {
      const grund = rechtFehlerOder(err);
      if (riegelGestartet) {
        zeigeFehler(`${grund} ${AUSGANG_UNKLAR}`);
      } else if (erfasst) {
        zeigeFehler(`${grund} Die Zeit steht im Tätigkeitsnachweis, abgerechnet wurde nichts — unter „Erfasste Zeiten" lässt sie sich später abrechnen.`);
      } else {
        zeigeFehler(grund);
      }
    } finally {
      buchungRef.current = false;
      setBuchungLaeuft(false);
      // Die Liste „Erfasste Zeiten" im Editor neu laden — in jedem Ausgang.
      setZeitenStand((n) => n + 1);
    }
    if (vormerkung) queueJournalMirror(vormerkung);
  };

  /**
   * Gutschrift einer Zeit nachholen, deren Riegel steht, deren Eintrag aber
   * im serviceLog fehlt (gbuZeitStand 'gutschrift_fehlt'). OHNE erneuten
   * Riegel-Aufruf: Position und Menge kommen aus dem Riegel selbst
   * (`gebuchtePositionId`, `gebuchteMenge`), die Id ist dieselbe stabile wie
   * beim ersten Versuch — ein zweites Nachholen findet den Eintrag und tut nichts.
   */
  const gutschriftNachholen = async (g: SifaGbu, z: GbuZeitZeile) => {
    if (isReadOnly || !onUpdate) return;
    if (buchungRef.current) return;               // Doppelklick: der Knopf ist waehrend Pruefung und Gutschrift gesperrt
    if (gbuZeitStand(z, meta?.globalData?.serviceLog) !== 'gutschrift_fehlt') {
      zeigeInfo('Für diese Zeit ist nichts nachzuholen.');
      setZeitenStand((n) => n + 1);
      return;
    }
    buchungRef.current = true;
    setBuchungLaeuft(true);
    zeigeInfo('Stand auf dem Server wird geprüft…');
    let vormerkung: JournalMirrorRequest | null = null;
    try {
      // Das lokale serviceLog kann veraltet sein (ein anderes Fenster hat schon nachgeholt und
      // gespeichert). Deshalb erst auf dem Server nachsehen — gutgeschrieben wird nur bei „fehlt".
      const entscheid = nachholEntscheid(await pruefeGutschriftAufServer(project.id, gbuZeitLogId(z.id)));
      if (!entscheid.gutschreiben) {
        zeigeFehler(entscheid.meldung);
        return;
      }
      const positionId = (z.gebuchtePositionId || '').trim();
      if (!alleServices.some((s) => s.id === positionId)) {
        zeigeFehler('Die LV-Position, auf die diese Zeit vermerkt ist, gibt es im Projekt nicht mehr. Bitte von der Verwaltung klären lassen — nicht von Hand nachbuchen.');
        return;
      }
      const ergebnis = rechneGbuBuchung(alleServices, positionId, Number(z.gebuchteMenge), {
        performerName: name || undefined,
        datum: z.datum,
        gbuNummer: g.nummer,
        gbuTitel: g.titel,
        fassung: g.fassung,
        taetigkeitId: z.id,
      });
      if (!ergebnis) {
        zeigeFehler('Die vermerkte LV-Position ist nicht mehr buchbar (Paket- oder Pauschalposition, oder Menge 0). Bitte von der Verwaltung klären lassen — nicht von Hand nachbuchen.');
        return;
      }
      vormerkung = schreibeGutschrift(ergebnis);
      zeigeInfo(`Gutschrift nachgeholt: ${ergebnis.menge.toLocaleString('de-DE')} ${ergebnis.stundenposition ? 'Std.' : 'Stück'} auf ${positionText(positionId)} (Zeit vom ${formatTag(z.datum)}).`);
    } catch (err) {
      zeigeFehler(rechtFehlerOder(err));
    } finally {
      buchungRef.current = false;
      setBuchungLaeuft(false);
      setZeitenStand((n) => n + 1);
    }
    if (vormerkung) queueJournalMirror(vormerkung);
  };

  /** „1.1 Erstellung GBU …" bzw. „die LV-Position". */
  const positionText = (positionId: string): string => {
    const p = alleServices.find((s) => s.id === positionId);
    return p ? `${p.lvPosition} ${p.title}` : 'die LV-Position';
  };

  /**
   * Leistungsliste und Log-Eintrag ins Projekt (onUpdate) und die
   * Zweitschrift ins Journal VORMERKEN — gestellt wird sie vom Aufrufer
   * hinter seinem try: ein Stolpern dort darf nie wie ein Fehlschlag der
   * Buchung aussehen.
   */
  const schreibeGutschrift = (ergebnis: NonNullable<ReturnType<typeof rechneGbuBuchung>>): JournalMirrorRequest | null => {
    if (!onUpdate) return null;
    const vorherigerLog = meta?.globalData?.serviceLog || [];
    onUpdate({
      ...project.data,
      metadata: {
        ...meta,
        globalData: {
          ...(meta?.globalData || { status: 'active' as const }),
          services: ergebnis.services,
          serviceLog: [...vorherigerLog, ergebnis.logEntry],
        },
      },
    });
    const projektId = (project.id || '').trim();
    if (!projektId || projektId === 'temp-id') {
      console.warn('[GbuModul] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
      return null;
    }
    return {
      projectId: projektId,
      entries: [ergebnis.logEntry],
      previousLog: vorherigerLog,
      source: GBU_JOURNAL_SOURCE,
      linkedProjectConfig: meta?.globalData?.linkedProjectConfig ?? null,
      // Eine GBU ist weder Zusatzleistung noch der Nachweis einer BEGEHUNG.
      statements: { [ergebnis.logEntry.id]: { isAddon: false, coversInspection: false } },
    };
  };

  // ─── Ansicht ──────────────────────────────────────────────────────────────

  const meldungZeile = meldung && (
    <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldungTon === 'fehler' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
      {meldungTon === 'fehler' && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
      <span className="flex-1 whitespace-pre-line">{meldung}</span>
      <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
    </div>
  );

  const reiterKnopf = (r: Reiter, label: string, Icon: typeof ClipboardList) => (
    <button key={r} onClick={() => setReiter(r)} className={`min-h-[40px] px-3 flex items-center justify-center gap-1.5 text-xs font-bold rounded-lg ${reiter === r ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <>
      {offenId ? (
        <div data-glass-view="sifa" className="flex flex-col h-full">
          {meldung && <div className="px-4 pt-3 bg-slate-50">{meldungZeile}</div>}
          <div className="flex-1 min-h-0">
            <GbuEditor
              key={offenId}
              gbuId={offenId}
              projectId={project.id}
              kataster={kataster}
              meta={freigabeMeta}
              branche={branche}
              companyName={companyName}
              benutzer={benutzer}
              heute={heute}
              isReadOnly={isReadOnly}
              isMobile={isMobile}
              onZurueck={() => { setOffenId(null); setMeldung(null); }}
              onGbuGeaendert={uebernehmeGbu}
              onGeloescht={(id) => { entferneGbu(id); setOffenId(null); zeigeInfo('Der Entwurf ist gelöscht.'); }}
              onZeitBuchen={zeitBuchen}
              kannAbrechnen={kannAbrechnen}
              zeitenStand={zeitenStand}
              buchungLaeuft={buchungLaeuft}
              onZeitAbrechnen={zeitAbrechnen}
              serviceLog={serviceLog}
              kannGutschreiben={!isReadOnly && !!onUpdate}
              onGutschriftNachholen={(g, z) => void gutschriftNachholen(g, z)}
            />
          </div>
        </div>
      ) : (
        <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
          <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
            <button onClick={onBack} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base font-bold text-slate-800 truncate">Gefährdungsbeurteilungen</h1>
              <p className="text-xs text-slate-500 truncate">{project.name || meta?.projectName}</p>
            </div>
          </div>

          <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
            {isReadOnly && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
                Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Beurteilungen, Maßnahmen und Betrieb lassen sich ansehen und ausgeben —
                anlegen, ändern, freigeben und Zeit buchen ist gesperrt.
              </div>
            )}

            {kataster.schemaFehlt ? (
              <div className="p-5 bg-white border border-amber-300 rounded-2xl flex items-start gap-3">
                <Database className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-bold text-slate-800">Noch nicht eingerichtet</p>
                  <p className="text-xs text-slate-600">
                    Datenbank-Update Nr. 167/168 fehlt — die Gefährdungsbeurteilungen stehen in dieser Umgebung erst danach zur Verfügung.
                    Bitte an die Verwaltung wenden.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-1 p-1 bg-slate-100 rounded-xl`}>
                  {reiterKnopf('beurteilungen', 'Beurteilungen', ClipboardList)}
                  {reiterKnopf('massnahmen', 'Maßnahmen', ListChecks)}
                  {reiterKnopf('betrieb', 'Betrieb', Building2)}
                </div>

                {meldungZeile}
                {fehler && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{fehler}</span>
                  </div>
                )}

                {reiter === 'beurteilungen' && (
                  <GbuListe
                    gbus={gbus}
                    bereiche={kataster.bereiche}
                    laden={laden}
                    heute={heute}
                    isReadOnly={isReadOnly}
                    isMobile={isMobile}
                    busy={anlegenLaeuft}
                    onNeu={neueGbu}
                    onOeffnen={(g) => { setMeldung(null); setOffenId(g.id); }}
                  />
                )}
                {reiter === 'massnahmen' && (
                  <MassnahmenAnsicht
                    gbus={gbus}
                    kataster={kataster}
                    kundeName={kundeName}
                    bueroName={freigabeMeta.bueroName}
                    heute={heute}
                    isReadOnly={isReadOnly}
                    isMobile={isMobile}
                    onOeffnen={(id) => { setMeldung(null); setOffenId(id); }}
                  />
                )}
                {reiter === 'betrieb' && (
                  <BetriebAnsicht
                    projectId={project.id}
                    kataster={kataster}
                    mitarbeiter={mitarbeiter}
                    gbus={gbus}
                    companyName={companyName}
                    heute={heute}
                    isReadOnly={isReadOnly}
                    isMobile={isMobile}
                    onKataster={aendereKataster}
                    onZuordnungen={setzeZuordnungen}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {anlegenOffen && (
        <NeueGbuDialog
          bereiche={kataster.bereiche}
          laeuft={anlegenLaeuft}
          onClose={() => setAnlegenOffen(false)}
          onAnlegen={(e) => void anlegen(e)}
          onZuBetrieb={() => { setAnlegenOffen(false); setReiter('betrieb'); }}
        />
      )}

      {zeitFuer && (
        <GbuZeitBuchen
          gbu={zeitFuer}
          nutzerName={name}
          heute={heute}
          kannAbrechnen={kannAbrechnen}
          keineStundenposition={!isReadOnly && billingServices.length === 0}
          laeuft={buchungLaeuft}
          onClose={() => setZeitFuer(null)}
          onWeiter={(e) => zeitEingegeben(zeitFuer, e)}
        />
      )}

      {buchung && (
        <BillingSelectionModal
          isOpen
          onClose={() => {
            const neu = buchung.quelle.art === 'neu';
            setBuchung(null);
            zeigeInfo(neu ? 'Abgebrochen — es wurde keine Zeit erfasst.' : 'Abgebrochen — nichts abgerechnet.');
          }}
          onConfirm={(serviceId, menge) => {
            const b = buchung;
            setBuchung(null);
            void zeitVerbuchen(b.gbu, b.quelle, serviceId, menge);
          }}
          services={billingServices}
          title={`Zeit an GBU Nr. ${buchung.gbu.nummer} abrechnen`}
          context="gbu"
          memoryScope={project.id}
          canSeeFinancials={rechte.hat('preise.sehen')}
          vorgabeMenge={viertelstunden(quellenZeit(buchung.quelle).dauerMinuten)}
          preferredIds={gbuVorauswahl}
          bewussteWahl={gbuVorauswahl.length === 0}
          vorabHinweis={vorabHinweisText({
            dauerText: formatDauer(quellenZeit(buchung.quelle).dauerMinuten),
            datumText: formatTag(quellenZeit(buchung.quelle).datum),
            stunden: viertelstunden(quellenZeit(buchung.quelle).dauerMinuten),
            stundenpositionen,
          })}
        />
      )}
    </>
  );
};
